import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { openInNewTab } from '../../utils/openNewTab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTasks,
  faClock,
  faCheckCircle,
  faExclamationTriangle,
  faBalanceScale,
  faListCheck,
  faHistory,
  faMagnifyingGlass,
  faFolderOpen,
  faExternalLinkAlt,
  faEdit,
  faSpinner,
  faList,
  faChevronLeft,
  faChevronRight,
  faCalendarAlt,
  faPlus,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../hooks/useAuth';
import { useHomeTasks, useRecentCases, useTaskCalendar, useCreateTask } from '../../hooks/useHome';
import { useSolicitors } from '../../hooks/useCaseFile';
import { useCaseTypes } from '../../hooks/useCases';
import {
  computeSummary,
  sortTasks,
  caseSummary,
  getCaseType,
  isTaskOverdue,
  isTaskDueToday,
  isTaskCompleted,
  statusClass,
  CATEGORY_CONFIG,
} from '../../utils/tasks';
import { formatDateTime } from '../../utils/dates';
import type {
  Task,
  CaseDetails,
  RecentCase,
  TaskCategory,
  CalendarDateCounts,
} from '../../types/home';
import EditTaskModal from './components/EditTaskModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import css from './HomePage.module.css';
import Pagination from '../../components/ui/Pagination/Pagination';

const PAGE_SIZE = 10;

/* ────────────────────────────────────────────── */
/*  Animated Counter                              */
/* ────────────────────────────────────────────── */
function AnimatedCounter({ value }: { value: number }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef<number | null>(null);

  useEffect(() => {
    const start = performance.now();
    const from = display;
    const duration = 800;

    function tick(now: number) {
      const progress = Math.min((now - start) / duration, 1);
      setDisplay(Math.floor(from + (value - from) * progress));
      if (progress < 1) ref.current = requestAnimationFrame(tick);
    }

    ref.current = requestAnimationFrame(tick);
    return () => {
      if (ref.current) cancelAnimationFrame(ref.current);
    };
    // Only re-animate when value changes
  }, [value]);

  return <>{display}</>;
}

/* ────────────────────────────────────────────── */
/*  SummaryCards                                   */
/* ────────────────────────────────────────────── */
function SummaryCards({ tasks }: { tasks: Task[] }) {
  const summary = useMemo(() => computeSummary(tasks), [tasks]);

  const cards: { label: string; value: number; icon: typeof faTasks; cls: string }[] = [
    { label: 'Total Tasks', value: summary.total, icon: faTasks, cls: css.iconTotal },
    { label: 'In Progress', value: summary.inProgress, icon: faClock, cls: css.iconOngoing },
    { label: 'Completed', value: summary.completed, icon: faCheckCircle, cls: css.iconCompleted },
    { label: 'Overdue', value: summary.overdue, icon: faExclamationTriangle, cls: css.iconOverdue },
  ];

  return (
    <section className={css.summaryGrid}>
      {cards.map((c) => (
        <div className={css.summaryCard} key={c.label}>
          <div className={`${css.summaryIcon} ${c.cls}`}>
            <FontAwesomeIcon icon={c.icon} />
          </div>
          <div>
            <div className={css.summaryNumber}>
              <AnimatedCounter value={c.value} />
            </div>
            <div className={css.summaryLabel}>{c.label}</div>
          </div>
        </div>
      ))}
    </section>
  );
}

/* ────────────────────────────────────────────── */
/*  TaskFilter                                     */
/* ────────────────────────────────────────────── */
interface TaskFilterProps {
  tasks: Task[];
  caseDetailsMap: Record<string, CaseDetails>;
  currentFilter: string | null;
  onFilter: (caseRef: string | null) => void;
}

function TaskFilter({ tasks, caseDetailsMap, currentFilter, onFilter }: TaskFilterProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const caseRefs = useMemo(
    () => [...new Set(tasks.map((t) => t.CaseRef).filter((r): r is string => !!r))],
    [tasks],
  );

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const filteredRefs = useMemo(() => {
    if (!search) return caseRefs;
    const s = search.toLowerCase();
    return caseRefs.filter((ref) => {
      const d = caseDetailsMap[ref];
      const defendant = d?.defendant_name ?? d?.Defendant ?? d?.defendant ?? '';
      return ref.toLowerCase().includes(s) || defendant.toLowerCase().includes(s);
    });
  }, [caseRefs, caseDetailsMap, search]);

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className={`${css.filterBtn} ${currentFilter ? css.filterBtnActive : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        title={currentFilter ? `Filtered by: ${currentFilter}` : 'Filter tasks by case'}
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} />
      </button>

      {open && (
        <div className={css.filterDropdown}>
          <div style={{ padding: '12px', borderBottom: '1px solid var(--gray-200)' }}>
            <input
              type="text"
              className={css.filterSearch}
              placeholder="Search cases..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div style={{ padding: '8px' }}>
            <div
              className={`${css.filterOption} ${currentFilter === null ? css.filterOptionActive : ''}`}
              onClick={() => {
                onFilter(null);
                setOpen(false);
              }}
            >
              <div className={css.filterOptionLabel}>
                <FontAwesomeIcon
                  icon={faList}
                  style={{ marginRight: 8, color: 'var(--primary-500)' }}
                />
                All Tasks
              </div>
            </div>
            {filteredRefs.map((ref) => {
              const d = caseDetailsMap[ref] ?? {};
              const defendant = d.defendant_name ?? d.Defendant ?? d.defendant ?? '';
              const caseType = d.CaseType ?? d.caseType ?? d.case_type ?? '';
              const label = defendant ? `${ref} - ${defendant}` : ref;
              return (
                <div
                  key={ref}
                  className={`${css.filterOption} ${currentFilter === ref ? css.filterOptionActive : ''}`}
                  onClick={() => {
                    onFilter(ref);
                    setOpen(false);
                  }}
                >
                  <div className={css.filterOptionLabel}>{label}</div>
                  {caseType && <div className={css.filterOptionSub}>{caseType}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────── */
/*  TaskItem                                       */
/* ────────────────────────────────────────────── */
interface TaskItemProps {
  task: Task;
  caseDetailsMap: Record<string, CaseDetails>;
  currentUserSub: string;
  onEdit: (task: Task) => void;
  caseTypeIdMap: Map<string, number>;
}

function TaskItem({ task, caseDetailsMap, currentUserSub, onEdit, caseTypeIdMap }: TaskItemProps) {
  const navigate = useNavigate();
  const summary = task.CaseRef ? caseSummary(task, caseDetailsMap) : 'Custom Task';
  const rawCaseType = getCaseType(task, caseDetailsMap);
  const overdue = isTaskOverdue(task);
  const sCls = statusClass(task.Status);
  const canEdit = currentUserSub && task.user_id === currentUserSub;
  const showAssignedBy = task.user_id !== task.task_from_user_id && task.from_display_name;
  const cat = (task.Category ?? 'misc') as TaskCategory;
  const catConf = CATEGORY_CONFIG[cat];

  const badgeMap: Record<string, string> = {
    inProgress: css.badgeInProgress,
    completed: css.badgeCompleted,
    default: css.badgeDefault,
  };

  function buildCaseUrl() {
    if (!task.CaseRef) return '';
    const typeId = caseTypeIdMap.get(rawCaseType.toLowerCase());
    const qs = typeId ? `?caseTypeId=${typeId}` : '';
    return `/cases/${encodeURIComponent(task.CaseRef)}${qs}`;
  }

  function openCase(e: React.MouseEvent) {
    e.preventDefault();
    const url = buildCaseUrl();
    if (url) navigate(url);
  }

  function openNewTab(e: React.MouseEvent) {
    e.preventDefault();
    const url = buildCaseUrl();
    if (url) openInNewTab(url);
  }

  return (
    <div className={css.taskItem}>
      <div className={css.taskContent}>
        <div className={css.taskTitle}>
          <span
            className={css.categoryBadge}
            style={{ background: catConf.bg, color: catConf.color }}
          >
            {catConf.label}
          </span>{' '}
          {task.Description}
        </div>
        <div className={css.taskMeta}>{summary}</div>
        <div className={css.taskDetails}>
          <span>Created: {formatDateTime(task.Timestamp)}</span>
          <span>Due: {formatDateTime(task.Deadline)}</span>
        </div>
        <div className={css.taskStatus}>
          <span className={`${css.badge} ${badgeMap[sCls] ?? css.badgeDefault}`}>
            {task.Status}
          </span>
          {overdue && <span className={`${css.badge} ${css.badgeOverdue}`}>Overdue</span>}
          {showAssignedBy && (
            <div className={css.assignedBy}>* Assigned by: {task.from_display_name}</div>
          )}
        </div>
      </div>

      <div className={css.taskActions}>
        {task.CaseRef && (
          <div className={css.taskActionRow}>
            <button
              className={`${css.btnSm} ${css.btnOutline} ${css.btnIcon}`}
              onClick={openNewTab}
              title="Open in new tab"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </button>
            <a
              href="#"
              className={`${css.btnSm} ${css.btnPrimary} ${css.btnAction}`}
              onClick={openCase}
            >
              <FontAwesomeIcon icon={faFolderOpen} /> Open
            </a>
          </div>
        )}
        {canEdit ? (
          <button
            className={`${css.btnSm} ${css.btnPrimary} ${css.btnAction}`}
            onClick={() => onEdit(task)}
          >
            <FontAwesomeIcon icon={faEdit} /> Edit Task
          </button>
        ) : (
          <div style={{ height: 32 }} />
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────── */
/*  CaseItem (expanded card)                      */
/* ────────────────────────────────────────────── */
function CaseItem({ c, caseTypeIdMap }: { c: RecentCase; caseTypeIdMap: Map<string, number> }) {
  const navigate = useNavigate();
  const ref = c.CaseRef ?? c.caseRef ?? '';
  const rawCaseType = c.CaseType ?? c.caseType ?? c.case_type ?? '';
  const defendant = c.defendant_name ?? c.Defendant ?? c.defendant ?? '';
  const clientParts = [c['First Name'], c['Middle Name'], c['Last Name']].filter(Boolean);
  const client = clientParts.join(' ');
  const stage = c.Stage ?? c.stage ?? '';

  function buildCaseUrl() {
    const typeId = caseTypeIdMap.get(rawCaseType.toLowerCase());
    const qs = typeId ? `?caseTypeId=${typeId}` : '';
    return `/cases/${encodeURIComponent(ref)}${qs}`;
  }

  const stageClass = (() => {
    const s = stage.toLowerCase();
    if (s.includes('pre-litigation') || s.includes('prelitigation')) return css.stagePreLitigation;
    if (s.includes('litigation')) return css.stageLitigation;
    if (s.includes('pre-action') || s.includes('preaction')) return css.stagePreAction;
    if (s.includes('settled') || s.includes('closed')) return css.stageSettled;
    return css.stageDefault;
  })();

  return (
    <div className={css.caseCardFull} onClick={() => navigate(buildCaseUrl())}>
      <div className={css.caseCardTop}>
        <span className={css.caseCardRef}>{ref}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          {stage && <span className={`${css.stageBadge} ${stageClass}`}>{stage}</span>}
          <FontAwesomeIcon
            icon={faExternalLinkAlt}
            className={css.newTabIcon}
            title="Open in new tab"
            onClick={(e) => {
              e.stopPropagation();
              openInNewTab(buildCaseUrl());
            }}
          />
        </div>
      </div>
      <div className={css.caseCardParties}>
        {client} vs {defendant}
      </div>
      <div className={css.caseCardBottom}>
        {rawCaseType && <span className={css.caseTypeTag}>{rawCaseType}</span>}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────── */
/*  MiniCalendar                                   */
/* ────────────────────────────────────────────── */
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DAY_LABELS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

function MiniCalendar({ calendarData }: { calendarData?: Record<string, CalendarDateCounts> }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [tooltip, setTooltip] = useState<{ dateStr: string; top: number; left: number } | null>(
    null,
  );
  const gridRef = useRef<HTMLDivElement>(null);

  function changeMonth(delta: number) {
    let m = month + delta;
    let y = year;
    if (m > 11) {
      m = 0;
      y++;
    }
    if (m < 0) {
      m = 11;
      y--;
    }
    setYear(y);
    setMonth(m);
    setTooltip(null);
  }

  const firstDay = new Date(year, month, 1);
  let startDay = firstDay.getDay();
  startDay = startDay === 0 ? 6 : startDay - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();

  const days: { day: number; current: boolean; dateStr: string }[] = [];
  for (let i = startDay - 1; i >= 0; i--) {
    const d = daysInPrev - i;
    const pm = month === 0 ? 11 : month - 1;
    const py = month === 0 ? year - 1 : year;
    days.push({
      day: d,
      current: false,
      dateStr: `${py}-${String(pm + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
    });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({
      day: d,
      current: true,
      dateStr: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
    });
  }
  const remaining = (7 - (days.length % 7)) % 7;
  for (let i = 1; i <= remaining; i++) {
    const nm = month === 11 ? 0 : month + 1;
    const ny = month === 11 ? year + 1 : year;
    days.push({
      day: i,
      current: false,
      dateStr: `${ny}-${String(nm + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`,
    });
  }

  function handleHover(e: React.MouseEvent, dateStr: string) {
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const gridRect = gridRef.current?.getBoundingClientRect();
    if (gridRect) {
      setTooltip({
        dateStr,
        top: rect.top - gridRect.top,
        left: rect.right - gridRect.left + 8,
      });
    }
  }

  const tooltipData = tooltip && calendarData ? calendarData[tooltip.dateStr] : null;

  return (
    <div className={css.calendarCard} style={{ position: 'relative' }}>
      <div className={css.calHeader}>
        <button className={css.calNavBtn} onClick={() => changeMonth(-1)}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
        <span className={css.calTitle}>
          {MONTH_NAMES[month]} {year}
        </span>
        <button className={css.calNavBtn} onClick={() => changeMonth(1)}>
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
      <div className={css.calGrid} ref={gridRef}>
        {DAY_LABELS.map((d) => (
          <div key={d} className={css.calDayLabel}>
            {d}
          </div>
        ))}
        {days.map((item, idx) => {
          const data = calendarData?.[item.dateStr];
          const isToday =
            item.current &&
            item.day === now.getDate() &&
            month === now.getMonth() &&
            year === now.getFullYear();
          const hasTasks = data && data.total > 0;
          const hasOverdue = hasTasks && data.overdue > 0;

          const cls = [
            css.calDay,
            !item.current && css.calDayOther,
            isToday && css.calDayToday,
            hasTasks && css.calDayHasTasks,
            hasOverdue && css.calDayOverdue,
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <div
              key={idx}
              className={cls}
              onMouseEnter={(e) => handleHover(e, item.dateStr)}
              onMouseLeave={() => setTooltip(null)}
            >
              {item.day}
            </div>
          );
        })}
        {tooltip && tooltipData && (
          <div
            className={`${css.calTooltip} ${css.calTooltipVisible}`}
            style={{ top: tooltip.top, left: tooltip.left }}
          >
            <div className={css.calTooltipDate}>
              {new Date(tooltip.dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              })}
            </div>
            <div className={css.calTooltipTotal}>
              {tooltipData.total}
              <span>tasks</span>
            </div>
            <div>
              {(['admin', 'client', 'court', 'defendant', 'misc'] as const).map((cat) => {
                const count = tooltipData[cat];
                if (!count) return null;
                const conf = CATEGORY_CONFIG[cat];
                return (
                  <div key={cat} className={css.calTooltipCat}>
                    <span className={css.calTooltipCatLabel}>
                      <span className={css.calTooltipDot} style={{ background: conf.color }} />
                      {conf.label}
                    </span>
                    <span className={css.calTooltipCatCount}>{count}</span>
                  </div>
                );
              })}
            </div>
            {tooltipData.overdue > 0 && (
              <div className={css.calTooltipOverdue}>⚠ {tooltipData.overdue} overdue</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   HomePage
   ════════════════════════════════════════════════ */
type TaskTab = 'today' | 'overdue' | 'upcoming' | 'completed';

export default function HomePage() {
  const { user } = useAuth();
  const { data: homeData, isLoading: tasksLoading, isError: tasksError } = useHomeTasks();
  const { data: recentCases, isLoading: casesLoading } = useRecentCases();
  const { data: allCaseTypes = [] } = useCaseTypes();

  // Calendar date range (current month ± 1 month)
  const now = new Date();
  const calStart = useMemo(() => {
    const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return d.toISOString().split('T')[0];
  }, [now.getFullYear(), now.getMonth()]);
  const calEnd = useMemo(() => {
    const d = new Date(now.getFullYear(), now.getMonth() + 2, 1);
    return d.toISOString().split('T')[0];
  }, [now.getFullYear(), now.getMonth()]);
  const { data: calendarData } = useTaskCalendar(calStart, calEnd);

  /* Map lowercase case-type name/display_name → numeric id */
  const caseTypeIdMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const ct of allCaseTypes) {
      if (ct.name) m.set(ct.name.toLowerCase(), ct.id);
      if (ct.display_name) m.set(ct.display_name.toLowerCase(), ct.id);
    }
    return m;
  }, [allCaseTypes]);

  const [activeTab, setActiveTab] = useState<TaskTab>('today');
  const [filter, setFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({
    description: '',
    deadline: '',
    category: 'misc' as string,
    assigned_to: '',
    allow_reminders: false,
    reminder_datetime: '',
  });

  const createTask = useCreateTask();
  const { data: solicitors = [] } = useSolicitors();

  const handleCreateTask = async () => {
    if (!taskForm.description.trim() || !taskForm.deadline) return;
    await createTask.mutateAsync({
      desc: taskForm.description,
      deadline: taskForm.deadline,
      category: taskForm.category,
      assigned_to: taskForm.assigned_to || undefined,
      allow_reminders: taskForm.allow_reminders,
      reminder_datetime: taskForm.reminder_datetime || undefined,
    });
    setCreateModalOpen(false);
    setTaskForm({
      description: '',
      deadline: '',
      category: 'misc',
      assigned_to: '',
      allow_reminders: false,
      reminder_datetime: '',
    });
  };

  const allTasks = homeData?.tasks ?? [];
  const caseDetailsMap = homeData?.caseDetailsMap ?? {};

  const filteredTasks = useMemo(
    () => (filter ? allTasks.filter((t) => t.CaseRef === filter) : allTasks),
    [allTasks, filter],
  );

  // Split tasks into tab groups
  const taskGroups = useMemo(() => {
    const today: Task[] = [];
    const overdue: Task[] = [];
    const upcoming: Task[] = [];
    const completed: Task[] = [];

    for (const t of filteredTasks) {
      if (isTaskCompleted(t.Status)) {
        completed.push(t);
      } else if (isTaskOverdue(t)) {
        overdue.push(t);
      } else if (isTaskDueToday(t)) {
        today.push(t);
      } else {
        upcoming.push(t);
      }
    }
    return { today, overdue, upcoming, completed };
  }, [filteredTasks]);

  const currentTabTasks = taskGroups[activeTab];
  const sorted = useMemo(() => sortTasks(currentTabTasks), [currentTabTasks]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageTasks = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const currentUserSub = user?.sub ?? '';

  const tabs: { key: TaskTab; label: string; count: number }[] = [
    { key: 'today', label: 'Today', count: taskGroups.today.length },
    { key: 'overdue', label: 'Overdue', count: taskGroups.overdue.length },
    { key: 'upcoming', label: 'Upcoming', count: taskGroups.upcoming.length },
    { key: 'completed', label: 'Completed', count: taskGroups.completed.length },
  ];

  return (
    <>
      {/* ── Welcome Banner ── */}
      <section className={css.welcome}>
        <div className={css.welcomeCard}>
          <div className={css.welcomeContent}>
            <h2 className={css.welcomeTitle}>Welcome back, {user?.username ?? 'User'}!</h2>
            <p className={css.welcomeSubtitle}>Track your tasks and recent cases</p>
          </div>
          <div className={css.welcomeIcon}>
            <FontAwesomeIcon icon={faBalanceScale} />
          </div>
        </div>
      </section>

      {/* ── Summary Cards ── */}
      <SummaryCards tasks={filteredTasks} />

      {/* ── Content Grid ── */}
      <div className={css.contentGrid}>
        {/* ── My Tasks (Tabbed) ── */}
        <div className={css.section}>
          <div className={css.sectionHeader}>
            <h3 className={css.sectionTitle}>
              <FontAwesomeIcon icon={faListCheck} /> My Tasks
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <button className={css.createTaskBtn} onClick={() => setCreateModalOpen(true)}>
                <FontAwesomeIcon icon={faPlus} /> Create Task
              </button>
              <Link to="/tasks" className={css.viewAllLink}>
                View All
              </Link>
              <TaskFilter
                tasks={allTasks}
                caseDetailsMap={caseDetailsMap}
                currentFilter={filter}
                onFilter={(ref) => {
                  setFilter(ref);
                  setPage(1);
                }}
              />
            </div>
          </div>

          {/* Tab Bar */}
          <div className={css.tabBar}>
            {tabs.map((t) => (
              <button
                key={t.key}
                className={`${css.tab} ${activeTab === t.key ? css.tabActive : ''}`}
                onClick={() => {
                  setActiveTab(t.key);
                  setPage(1);
                }}
              >
                {t.label}
                <span className={css.tabCount}>{t.count}</span>
              </button>
            ))}
          </div>

          <div className={css.sectionContent}>
            {tasksLoading && (
              <div className={css.emptyState}>
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                <p>Loading tasks...</p>
              </div>
            )}
            {tasksError && (
              <div className={css.emptyState}>
                <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
                <p>Error loading tasks</p>
              </div>
            )}
            {!tasksLoading && !tasksError && sorted.length === 0 && (
              <div className={css.emptyState}>
                <FontAwesomeIcon icon={faTasks} size="2x" />
                <p>No {activeTab} tasks</p>
              </div>
            )}
            {pageTasks.map((task) => (
              <TaskItem
                key={task.TaskID}
                task={task}
                caseDetailsMap={caseDetailsMap}
                currentUserSub={currentUserSub}
                onEdit={setEditingTask}
                caseTypeIdMap={caseTypeIdMap}
              />
            ))}
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </div>

        {/* ── Sidebar: Calendar + Recent Cases ── */}
        <div>
          {/* Mini Calendar */}
          <div className={css.section} style={{ marginBottom: 'var(--space-4)' }}>
            <div className={css.sectionHeader}>
              <h3 className={css.sectionTitle}>
                <FontAwesomeIcon icon={faCalendarAlt} /> Calendar
              </h3>
            </div>
            <div className={css.sectionContent}>
              <MiniCalendar calendarData={calendarData} />
            </div>
          </div>

          {/* Recent Cases */}
          <div className={css.section}>
            <div className={css.sectionHeader}>
              <h3 className={css.sectionTitle}>
                <FontAwesomeIcon icon={faHistory} /> Recent Cases
              </h3>
            </div>
            <div className={css.sectionContent}>
              {casesLoading && (
                <div className={css.emptyState}>
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                  <p>Loading recent cases...</p>
                </div>
              )}
              {!casesLoading && (!recentCases || recentCases.length === 0) && (
                <div className={css.emptyState}>
                  <FontAwesomeIcon icon={faHistory} size="2x" />
                  <p>No recently viewed cases</p>
                </div>
              )}
              {recentCases?.map((c) => {
                const ref = c.CaseRef ?? c.caseRef ?? '';
                return <CaseItem key={ref} c={c} caseTypeIdMap={caseTypeIdMap} />;
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {editingTask && (
        <EditTaskModal
          task={editingTask}
          onClose={() => setEditingTask(null)}
          onDelete={() => {
            setDeletingTaskId(editingTask.TaskID);
          }}
        />
      )}

      {deletingTaskId && (
        <DeleteConfirmModal
          taskID={deletingTaskId}
          onClose={() => setDeletingTaskId(null)}
          onDeleted={() => {
            setDeletingTaskId(null);
            setEditingTask(null);
          }}
        />
      )}

      {/* ── Create Task Modal ── */}
      {createModalOpen && (
        <div className={css.modalOverlay} onClick={() => setCreateModalOpen(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h2>Create Task</h2>
              <button className={css.modalClose} onClick={() => setCreateModalOpen(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.formGroup}>
                <label>Description *</label>
                <textarea
                  rows={3}
                  className={css.formInput}
                  placeholder="Task description…"
                  value={taskForm.description}
                  onChange={(e) => setTaskForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>
              <div className={css.formGroup}>
                <label>Assign To</label>
                <select
                  className={css.formInput}
                  value={taskForm.assigned_to}
                  onChange={(e) => setTaskForm((p) => ({ ...p, assigned_to: e.target.value }))}
                >
                  <option value="">Myself</option>
                  {solicitors.map((s) => (
                    <option key={s.cognito_user_id} value={s.cognito_user_id}>
                      {s.displayName || s.username}
                    </option>
                  ))}
                </select>
              </div>
              <div className={css.formGroup}>
                <label>Category</label>
                <select
                  className={css.formInput}
                  value={taskForm.category}
                  onChange={(e) => setTaskForm((p) => ({ ...p, category: e.target.value }))}
                >
                  <option value="admin">Admin</option>
                  <option value="client">Client</option>
                  <option value="court">Court</option>
                  <option value="defendant">Defendant</option>
                  <option value="misc">Miscellaneous</option>
                </select>
              </div>
              <div className={css.formGroup}>
                <label>Deadline *</label>
                <input
                  type="datetime-local"
                  className={css.formInput}
                  value={taskForm.deadline}
                  onChange={(e) => setTaskForm((p) => ({ ...p, deadline: e.target.value }))}
                />
              </div>
              <div className={css.formGroup}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={taskForm.allow_reminders}
                    onChange={(e) =>
                      setTaskForm((p) => ({ ...p, allow_reminders: e.target.checked }))
                    }
                  />
                  Enable Reminder
                </label>
              </div>
              {taskForm.allow_reminders && (
                <div className={css.formGroup}>
                  <label>Reminder Date/Time</label>
                  <input
                    type="datetime-local"
                    className={css.formInput}
                    value={taskForm.reminder_datetime}
                    onChange={(e) =>
                      setTaskForm((p) => ({ ...p, reminder_datetime: e.target.value }))
                    }
                  />
                </div>
              )}
            </div>
            <div className={css.modalFooter}>
              <button className={css.cancelBtn} onClick={() => setCreateModalOpen(false)}>
                Cancel
              </button>
              <button
                className={css.createTaskBtn}
                disabled={
                  !taskForm.description.trim() || !taskForm.deadline || createTask.isPending
                }
                onClick={handleCreateTask}
              >
                {createTask.isPending ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faPlus} />
                )}{' '}
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
